import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, Box, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails, Grid, Card, CardActionArea, CardContent } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  List as ListIcon,
  Settings as SettingsIcon,
  Description as ReportIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import ClassesList from '../system-settings/ClassesList';
import MastersList from '../system-settings/MastersList';
import InstituteProfile from '../system-settings/InstituteProfile';
import CasteMasterList from '../system-settings/CasteMasterList';
import FeeHeadMaster from '../system-settings/FeeHeadMaster';
import HouseMaster from '../system-settings/HouseMaster';
import CategoryMaster from '../system-settings/CategoryMaster';
import PaymentModeMaster from '../system-settings/PaymentModeMaster';
import ClassGroupMaster from '../system-settings/ClassGroupMaster';
import ClassMaster from '../system-settings/ClassMaster';
import SectionMaster from '../system-settings/SectionMaster';
import ClassSubjectMaster from '../system-settings/SubjectMaster';
import DocumentMaster from '../system-settings/DocumentMaster';
import FeeInstallmentMaster from '../system-settings/FeeInstallmentMaster';
import LeavingReasonMaster from '../system-settings/LeavingReasonMaster';
import FeeStructureMaster from '../system-settings/FeeStructureMaster';
import SignatureMaster from '../system-settings/SignatureMaster';
import GlobalSubjectMaster from '../system-settings/GlobalSubjectMaster';
import ExamTypeMaster from '../system-settings/ExamTypeMaster';
import BloodGroupMaster from '../system-settings/BloodGroupMaster';
import ExamBoardMaster from '../system-settings/ExamBoardMaster';
import StudentTypeMaster from '../system-settings/StudentTypeMaster';
import { REPORT_TYPES } from '../system-settings/report-settings/config';
import AcademicSessionList from '../system-settings/academic-session/AcademicSessionList';

/**
 * System Settings Page (Admin)
 * Complete System Settings implementation with ALL masters and settings
 * */
const SystemSettings: React.FC<{ defaultExpanded?: string }> = ({ defaultExpanded }) => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState<string | false>(defaultExpanded || false);
  const [sessionTab, setSessionTab] = useState(0);

  const handleAccordionChange = (section: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? section : false);
  };

  const handleSessionTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSessionTab(newValue);
  };

  const masterTypes = [
    // Phase 1 Masters
    { type: 'medium', label: 'Medium', hasDefault: true },
    { type: 'religion', label: 'Religion' },
    { type: 'nationality', label: 'Nationality' },
    { type: 'motherTongue', label: 'Mother Tongue' },
    { type: 'familyRelation', label: 'Family Relation' },
    { type: 'childStatus', label: 'Child Status' },
    { type: 'bank', label: 'Name of Bank' },
    
    // Phase 2 Masters
    { type: 'reference', label: 'Reference' },
    { type: 'parentServiceType', label: 'Parent Service Type' },
    { type: 'staffReference', label: 'Staff Reference' },

    // Existing/Other Masters
    { type: 'caste', label: 'Caste', hasDefault: true },
    { type: 'category', label: 'Category', hasDefault: true },
    { type: 'house', label: 'House' },
    { type: 'document', label: 'Document' },
    { type: 'feeHead', label: 'Fee Head' },
    { type: 'feeInstallment', label: 'Fee Installment' },
    { type: 'leavingReason', label: 'Leaving Reason' },
    // { type: 'blood-group', label: 'Blood Group' },
    // { type: 'exam-board', label: 'Exam Board' },
  ];

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          System Settings
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Configure all master data, classes, sections, fee structures, and system settings for your school.
          <strong> All other modules depend on these settings.</strong>
        </Typography>

        <Box sx={{ mt: 3 }}>
          {/* Institute Profile */}
          <Accordion expanded={expandedSection === 'institute'} onChange={handleAccordionChange('institute')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SchoolIcon />
                <Typography variant="h6">Institute Profile</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="textSecondary" paragraph>
                Configure school name, address, contact details, logo, academic board, and school identifiers.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <InstituteProfile />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* List of Masters */}
          <Accordion expanded={expandedSection === 'masters'} onChange={handleAccordionChange('masters')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ListIcon />
                <Typography variant="h6">List of Masters</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Dedicated Masters */}
                <SignatureMaster />
                <CasteMasterList />
                <FeeHeadMaster />
                <HouseMaster />
                <CategoryMaster />
                <PaymentModeMaster />
                <ClassGroupMaster />
                <FeeInstallmentMaster />
                <LeavingReasonMaster />
                <DocumentMaster />
                <GlobalSubjectMaster />
                <ExamTypeMaster />
                <BloodGroupMaster />
                <ExamBoardMaster />
                <StudentTypeMaster />
                
                {/* Generic Masters */}
                {masterTypes
                  .filter(
                    (master) =>
                      master.type !== 'caste' &&
                      master.type !== 'feeHead' &&
                      master.type !== 'house' &&
                      master.type !== 'category' &&
                      master.type !== 'paymentMode' &&
                      master.type !== 'classGroup' &&
                      master.type !== 'feeInstallment' &&
                      master.type !== 'leavingReason' &&
                      master.type !== 'document',
                  )
                  .map((master) => (
                    <MastersList 
                        key={master.type} 
                        masterType={master.type} 
                        masterLabel={master.label} 
                        hasDefault={master.hasDefault} 
                    />
                  ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Session Configuration */}
          <Accordion expanded={expandedSection === 'session'} onChange={handleAccordionChange('session')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SettingsIcon />
                <Typography variant="h6">Session Configuration</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={sessionTab} onChange={handleSessionTabChange}>
                  <Tab label="Classes" />
                  <Tab label="Sections" />
                  <Tab label="Academic Sessions" />
                  <Tab label="Class Group" />
                  <Tab label="Subjects (Class-wise)" />
                  <Tab label="Documents" />
                  <Tab label="Fee Structure" />
                </Tabs>
              </Box>

              {sessionTab === 0 && <ClassMaster />}
              {sessionTab === 1 && <SectionMaster />}
              {sessionTab === 2 && <AcademicSessionList />}
              {sessionTab === 3 && <ClassGroupMaster />}
              {sessionTab === 4 && <ClassSubjectMaster />}
              {sessionTab === 5 && <DocumentMaster />}
              {sessionTab === 6 && <FeeStructureMaster />}
            </AccordionDetails>
          </Accordion>

          {/* Report Settings */}
          <Accordion expanded={expandedSection === 'reports'} onChange={handleAccordionChange('reports')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ReportIcon />
                <Typography variant="h6">Report Settings</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="textSecondary" paragraph>
                Configure report formats and templates. Click on a card to edit the template.
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(REPORT_TYPES).map(([key, config]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardActionArea onClick={() => navigate(`/report-settings/${key}`)} sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            {config.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {config.code}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* User & Roles Management */}
          <Accordion expanded={expandedSection === 'user-roles'} onChange={handleAccordionChange('user-roles')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon />
                <Typography variant="h6">User & Roles Management</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="textSecondary" paragraph>
                Manage users, roles, permissions, and view audit logs.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardActionArea onClick={() => navigate('/system-settings/user-roles/users')}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon color="primary" fontSize="large" />
                        <Typography variant="h6">Users</Typography>
                        <Typography variant="body2" color="textSecondary" align="center">
                          Manage system users and their access
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardActionArea onClick={() => navigate('/system-settings/user-roles/roles')}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <SettingsIcon color="primary" fontSize="large" />
                        <Typography variant="h6">Roles & Permissions</Typography>
                        <Typography variant="body2" color="textSecondary" align="center">
                          Configure roles and access rights
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Communication Settings */}
          <Accordion expanded={expandedSection === 'communication'} onChange={handleAccordionChange('communication')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmailIcon />
                <Typography variant="h6">Communication Settings</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="textSecondary" paragraph>
                Configure SMS, Email, and WhatsApp gateways and manage message templates.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardActionArea onClick={() => navigate('/system-settings/communication')}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <EmailIcon color="primary" fontSize="large" />
                        <Typography variant="h6">Communication Config</Typography>
                        <Typography variant="body2" color="textSecondary" align="center">
                          SMS, Email, WhatsApp Setup & Templates
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Payment Settings */}
          <Accordion expanded={expandedSection === 'payment'} onChange={handleAccordionChange('payment')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PaymentIcon />
                <Typography variant="h6">Payment Settings</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="textSecondary" paragraph>
                Configure payment gateway settings, POS settings, and view transaction reports.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardActionArea onClick={() => navigate('/system-settings/payments')}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon color="primary" fontSize="large" />
                        <Typography variant="h6">Payment Config & Reports</Typography>
                        <Typography variant="body2" color="textSecondary" align="center">
                          Gateway, POS, and Transaction Reports
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Logs & Audit */}
          <Accordion expanded={expandedSection === 'logs'} onChange={handleAccordionChange('logs')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <HistoryIcon />
                <Typography variant="h6">Logs & Audit</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardActionArea onClick={() => navigate('/system-settings/logs')}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon color="primary" fontSize="large" />
                        <Typography variant="h6">Logs & Audit Dashboard</Typography>
                        <Typography variant="body2" color="textSecondary" align="center">
                          Centralized view of all user actions and system changes.
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Container>
    </MainLayout>
  );
};

export default SystemSettings;
